// English content for /en/features and its task pages. Same shape as the
// Chinese bundle in featuresContent.js (ZH), so the page and the search
// version (api/features-og.js) render either language from one code path.
// Keep claims identical to the Chinese copy and true to App.jsx PLANS.
// Screenshots are the demo shop (Chinese UI), numbers redacted.

const HERO = {
  kicker: "Booking software for pet grooming shops",
  title: "Less time on WhatsApp, more time grooming.",
  lead: "Customers pick the service and time themselves, prices are worked out for them, and WhatsApp confirms the booking. You focus on grooming; the bookings come in on their own.",
};

const FEATURES = [
  {
    title: "Prices by weight, coat and breed — automatically",
    more: "price-list",
    body: "A grooming shop doesn't have one price: a 5 kg poodle and a 25 kg golden retriever, a short coat and a double coat, all cost different amounts. PettoBook was built that way from the start.",
    points: [
      "Pick a setup template (Simple / Standard / Detailed / Cats) and your services and prices are ready in 3 minutes",
      "Size × coat × breed — every cell can be edited",
      "Customers choose their pet and see its price straight away — no more “how much is it?”",
      "Special prices for individual regulars",
    ],
    badges: [["All plans", true]],
    shots: ["o6-templates-crop.jpg", "c2-service.jpg"],
    phoneSecond: true,
  },
  {
    title: "WhatsApp confirmations, sent for you",
    more: "whatsapp",
    video: ["ai-chat-en.mp4", "ai-chat-en-poster.jpg"],   // the side-by-side AI booking demo replaces the static screenshot
    body: "When a customer books online, the system confirms it from your shop's own WhatsApp number — customer, pet, date, time and service — without you typing a word.",
    points: [
      "Uses Meta-approved official templates, so it isn't treated as spam",
      "Every WhatsApp chat in one inbox that the whole team can share",
      "Want AI to answer prices and times and book for customers? Add WhatsApp AI auto-reply — a separately paid third-party Chatwoot or WATI account is required",
    ],
    badges: [["Pro and above", false], ["AI auto-reply: Chatwoot / WATI, paid separately", false]],
    shots: ["o11-wa-confirm-crop.jpg"],
  },
  {
    title: "24-hour self-booking, even after you close",
    more: "online-booking",
    body: "Customers choose the service, add-ons, groomer and time themselves. Only the slots you are really free for are shown — no double bookings, no overbooking.",
    points: [
      "Free slots are worked out from each groomer's shifts and existing bookings",
      "Add-ons (nail trim, teeth brushing, spa) are added to the total automatically",
      "Customers must accept your booking terms and deposit policy before booking",
    ],
    badges: [["Free: 10 a month · unlimited from Basic", false]],
    shots: ["c5-time.jpg", "c8-done.jpg"],
    phones: true,
  },
  {
    title: "A price list in one tap, shared with one QR code",
    more: "price-list",
    body: "Change a price and there's no need to get the poster redesigned. Your price list is laid out automatically from your prices, in 8 colour themes — download PNG / JPEG or print it.",
    points: [
      "Price list: dogs, cats, spa and add-ons, organised page by page",
      "Booking QR poster: put it in the shop or on Instagram and customers scan straight into your booking page",
    ],
    badges: [["All plans", true]],
    shots: ["o7-pricelist.jpg", "o4-qr-crop.jpg"],
  },
  {
    title: "A shop directory that brings you new customers",
    more: "directory",
    body: "PettoBook has a public directory of grooming shops. Pet owners nearby can find you by district, see your photos and services, and book directly.",
    points: [
      "Listed automatically when you open your shop — free",
      "Already in the directory? Claim your listing by uploading your business registration",
    ],
    badges: [["All plans", true]],
    shots: ["c1-shop.jpg", "c3-spa.jpg"],
    phones: true,
  },
  {
    title: "From check-in to payment, on one page",
    more: "daily",
    body: "Calendar, customers, pet notes, payments, credit, points and packages — all in one place. No more juggling Excel, WhatsApp and a notebook.",
    points: [
      "Booking detail: Arrived → Grooming → Done → Report, one tap each",
      "Walk-ins checked in within seconds",
      "Works on phone, tablet and computer",
    ],
    badges: [["Calendar & customers: all plans", true], ["Credit & packages: Pro and above", false]],
    shots: ["o3-detail.jpg"],
  },
];

const BOOKING_STEPS = [
  ["c1-shop.jpg", "① Open your shop", "Scan the QR or tap the Instagram link; sign in with Google or Apple in one tap"],
  ["c2-service.jpg", "② Choose a service", "The price is worked out from the pet's weight and breed"],
  ["c3-spa.jpg", "③ Spa upgrade", "Optional, each with a clear description"],
  ["c4-addons.jpg", "④ Add-ons", "Nail trim, teeth brushing and more; included items are marked"],
  ["c5-time.jpg", "⑤ Pick a time", "Only genuinely free slots are shown"],
  ["c6-confirm.jpg", "⑥ Confirm", "Use packages or points, and add grooming requests"],
  ["c7-terms.jpg", "⑦ Accept the terms", "Deposit, rescheduling and lateness policies, stated once and clearly"],
  ["c8-done.jpg", "⑧ Booked", "It appears in your calendar straight away"],
];

const DAY = [
  ["o10-dayflow-crop.jpg", "Opening", "Today at a glance", "One row per groomer: which bookings are paid and which aren't, with today's takings in the top corner."],
  ["o9-walkin-crop.jpg", "A walk-in arrives", "Checked in within seconds", "Find the customer or add a new one on the spot, then tap “Check in & start grooming”."],
  ["o3-detail.jpg", "Grooming", "One tap to change status", "Arrived, grooming, done — and the pet's allergies and notes are right there when you open it."],
  ["o8-customers-crop.jpg", "A regular returns", "Customer records saved automatically", "Each customer's pets, credit, points and past bookings — search by name or phone to find them."],
];

const FAQ = [
  ["I'm not good with computers. Is it hard to use?", "Pick a setup template and your prices are filled in for you — change a few numbers and you're ready. Day to day, you look at the calendar and tap to change a booking's status. If anything is unclear, see the help centre or contact us."],
  ["My customers are used to WhatsApp. Will they book online?", "PAWSIN's customers were WhatsApp users too. Now 94% of its bookings are made online by customers themselves, and 99 were made between 9 pm and 9 am. You can keep using WhatsApp as well — confirmations are sent from your own number."],
  ["I already have hundreds of customers. Do I have to re-enter them?", "No need to do it all at once. A customer's record is created automatically the first time they book online, and walk-ins can be added in seconds."],
  ["Is it really free?", "Opening your shop, the directory listing, the calendar and customer management are free. Customers can also book online themselves — 10 bookings a month are free so you can try it, and unlimited from the Basic plan at HK$128 (about US$16) a month. New shops also get a 14-day free Pro trial, no card needed."],
  ["Is customer data safe?", "Each shop's data is kept separate — other shops can't see your customers. All connections are encrypted."],
  ["Can I use it on my phone?", "Yes. PettoBook works on phones, tablets and computers — open it in a browser, or add it to your home screen."],
  ["Does the POS cost extra?", "No. The POS is included from the Basic plan: Basic has 100 products; Pro has 500 plus stock, refunds and exchanges, shifts and promotions; Enterprise has unlimited products plus purchasing and AI product setup."],
];

const PLANS = [
  ["Free", "0", ["Open your shop + directory listing", "Appointment scheduling (calendar)", "10 online bookings a month (to try it)", "Customer & pet records", "Price list & QR poster", "1 groomer"]],
  ["Basic", "128", ["Unlimited 24-hour online booking", "Automatic notifications", "Business analytics", "POS (100 products)", "3 groomers"]],
  ["Pro", "268", ["WhatsApp booking confirmations", "Credit, packages & digital stamp card", "In-app AI assistant for owners", "POS + stock & shifts (500 products)", "5 groomers"], true],
  ["Enterprise", "528", ["Loyalty points", "Your own branded app", "Full POS, unlimited products", "8 groomers"]],
];

const TASK_PAGES = [
  {
    slug: "daily", tab: "Daily running", kicker: "Daily running",
    title: "From opening to closing, one app runs it all.",
    lead: "Today's bookings, walk-ins, grooming progress and customer records, all in one place. No more jumping between a notebook, WhatsApp and Excel.",
    badges: [["Calendar & customers: all plans", true]],
    video: ["daily-en.mp4", "daily-en-poster.jpg"],
    steps: [
      ["o10-dayflow-crop.jpg", "09:00 Opening: today at a glance", "One row per groomer, paid and unpaid clearly marked, today's takings in the top corner."],
      ["o9-walkin-crop.jpg", "A walk-in: checked in within seconds", "Find a regular or add someone new, then tap “Check in & start grooming”."],
      ["o3-detail.jpg", "Grooming: one tap to change status", "Arrived → Grooming → Done; allergies and notes show as soon as you open it."],
      ["o8-customers-crop.jpg", "Regulars: records saved automatically", "Pets, credit, points and past bookings — search by name or phone."],
    ],
    points: [
      "Each groomer's bookings on their own row — no double bookings",
      "Walk-ins, online bookings and phone bookings in one calendar",
      "One booking page shows everything: customer, pet, service, price, payment",
      "Works on phone, tablet and computer",
    ],
    faq: [0, 5, 2],
    seo: {
      title: "Pet Grooming Shop Management Software | Calendar, Walk-ins, Status · PettoBook",
      description: "Run your grooming shop day in one app: a calendar per groomer, walk-ins checked in within seconds, one-tap grooming status and customer records saved automatically. Works on phone, tablet and computer. Start free.",
      keywords: "pet grooming shop software,grooming salon management,grooming appointment calendar,groomer scheduling,pet salon management software",
    },
  },
  {
    slug: "pos", tab: "POS", kicker: "Point of sale",
    title: "Grooming and retail, one receipt.",
    lead: "Booking payments, shampoo and treats, change, receipts, stock and reports in the same app. The grooming fee comes straight from the booking, and the customer's wallet and points work too.",
    badges: [["Included from Basic", false], ["Stock, refunds & shifts: Pro and above", false], ["Purchasing & AI product setup: Enterprise", false]],
    video: ["pos-en.mp4", "pos-en-poster.jpg"],
    steps: [
      ["pos-cart.jpg", "Grooming + retail in one sale", "Add today's unpaid bookings in one tap, then tap or scan products; shop offers apply automatically."],
      ["pos-pay.jpg", "Change worked out for you", "Cash, card, FPS or wallet — or split between them. The booking is marked paid at once."],
      ["pos-stock.jpg", "Stock comes off as you sell", "Stock, cost and low-stock alerts per item; receive, count and write off stock here too."],
      ["pos-report.jpg", "Reports at a glance", "Grooming and retail counted apart, payment methods for the till, best sellers and daily takings."],
    ],
    points: [
      "Booking payments and retail on one till — no second machine",
      "Scan barcodes with the phone camera or a Bluetooth scanner",
      "Open and close shifts with a cash count and Z report (Pro and above)",
      "Payments taken on a booking also show in the reports",
    ],
    faq: [6, 5],
    seo: {
      title: "POS for Pet Grooming Shops | Booking Payments, Retail, Stock, Reports · PettoBook",
      description: "A POS for pet grooming shops: grooming and retail on one receipt, cash change, card and wallet payments, automatic stock, and reports that split grooming from retail. Included from the Basic plan.",
      keywords: "pet shop POS,grooming salon POS,pet store point of sale,grooming shop till,pet shop stock management",
    },
  },
  {
    slug: "online-booking", tab: "Online booking", kicker: "24-hour online booking",
    title: "Closed for the night? Customers still book.",
    lead: "Customers pick the service, add-ons, groomer and time themselves. Only your genuinely free slots are shown — no double bookings, no overbooking — and the booking is in your calendar when you open next morning.",
    badges: [["Free: 10 a month · unlimited from Basic", false]],
    shots: { phones: true, shots: ["c5-time.jpg", "c8-done.jpg"] },
    stepsTitle: "How customers book: 8 steps, 1 minute",
    stepsPhones: true,
    steps: "BOOKING_STEPS",
    points: [
      "Free slots worked out from each groomer's shifts and existing bookings",
      "Add-ons (nail trim, teeth brushing, spa) added to the total automatically",
      "Customers must accept your terms and deposit policy before booking",
      "Customers can sign in with Google or Apple in one tap",
    ],
    proof: true,
    faq: [1, 3],
    seo: {
      title: "Online Booking for Pet Grooming Shops | 24/7 Self-booking · PettoBook",
      description: "Online booking for grooming shops: customers choose the service, add-ons, groomer and time 24/7, and only genuinely free slots are shown. At a real shop, 94% of bookings are now made online by customers.",
      keywords: "pet grooming online booking,grooming booking system,dog grooming appointment booking,24/7 online booking,salon booking software",
    },
  },
  {
    slug: "price-list", tab: "Pricing & price list", kicker: "Automatic pricing & price list",
    title: "Prices worked out for you, a price list in one tap.",
    lead: "A 5 kg poodle and a 25 kg golden retriever, a short coat and a double coat — they all cost different amounts. Set it up once: customers see their pet's price as soon as they pick it, and your price list updates when you change a price.",
    badges: [["All plans", true]],
    shots: { phoneSecond: true, shots: ["o6-templates-crop.jpg", "c2-service.jpg"] },
    steps: [
      ["o6-templates-crop.jpg", "① Pick a template", "Simple / Standard / Detailed / Cats — services and prices ready in 3 minutes."],
      ["c2-service.jpg", "② Customers pick their pet", "The price is worked out from weight, coat and breed."],
      ["o7-pricelist.jpg", "③ Generate the price list", "Dogs, cats, spa and add-ons on separate pages, 8 colour themes, PNG / JPEG or print."],
      ["o4-qr-crop.jpg", "④ Share the booking QR", "Put it in the shop or on Instagram; customers scan straight into your booking page."],
    ],
    points: [
      "Size × coat × breed — every cell can be edited",
      "Special prices for individual regulars",
      "No more customers asking “how much is it?”",
      "Change a price without redesigning the poster",
    ],
    faq: [0, 3],
    seo: {
      title: "Pet Grooming Price List Maker | Prices by Weight & Breed · PettoBook",
      description: "Generate a grooming price list in one tap: prices worked out by weight, coat and breed, 8 colour themes, download PNG / JPEG or print, plus a booking QR poster. On every plan. Start free.",
      keywords: "pet grooming price list,grooming price list template,dog grooming prices by weight,grooming menu maker,booking QR code",
    },
  },
  {
    slug: "whatsapp", tab: "WhatsApp confirmations", kicker: "Automatic WhatsApp confirmations",
    title: "Less time on WhatsApp, more time grooming.",
    lead: "When a customer books online, the system confirms it from your shop's own WhatsApp number — customer, pet, date, time and service — without you typing a word.",
    badges: [["Pro and above", false], ["AI auto-reply: Chatwoot / WATI, paid separately", false]],
    shots: { shots: ["o11-wa-confirm-crop.jpg"] },
    steps: [
      ["c8-done.jpg", "① A customer books online", "They pick a service and time on your shop's booking page."],
      ["o11-wa-confirm-crop.jpg", "② The confirmation goes out", "A Meta-approved official template, sent from your own number, so it isn't treated as spam."],
      ["o10-dayflow-crop.jpg", "③ It's in your calendar", "No reply needed — the booking is already on the groomer's row."],
    ],
    aiDemo: {
      title: "Add-on AI auto-reply: customers book on WhatsApp, the AI handles it",
      sub: "A demo conversation: the AI says it's an AI assistant, asks only for name and weight, quotes from your price list, checks times that are really free, then books it for you to confirm with one tap. Needs a separately paid third-party Chatwoot or WATI account.",
      video: ["ai-chat-en.mp4", "ai-chat-en-poster.jpg"],
    },
    points: [
      "Every WhatsApp chat in one inbox that the whole team can share",
      "Want AI to answer prices and times and book for customers? Add WhatsApp AI auto-reply — a separately paid third-party Chatwoot or WATI account is required",
    ],
    faq: [1, ["Do I need a separate WhatsApp Business account?", "Yes. Confirmations are sent from your shop's own number, so you connect WhatsApp Business (Meta, WATI or Chatwoot) in Settings → Messaging and use a Meta-approved template. WhatsApp template message fees are charged by Meta or your provider and are not included in PettoBook's monthly fee. The help centre walks you through the setup."]],
    seo: {
      title: "WhatsApp Booking Confirmations for Grooming Shops · PettoBook",
      description: "When a customer books online, a confirmation goes out automatically from your shop's own WhatsApp number: customer, pet, date, time and service. Meta-approved templates, every chat in one inbox. Pro and above.",
      keywords: "WhatsApp booking confirmation,WhatsApp auto reply grooming,WhatsApp Business booking,automatic appointment confirmation,grooming shop WhatsApp",
    },
  },
  {
    slug: "customers", tab: "Customers & packages", kicker: "Customers, credit & packages",
    title: "Customer records, credit and packages, on one page.",
    lead: "Each customer's pets, credit, points, packages and past bookings are saved automatically. Customers can use packages and points when they book, and pay with credit — no more checking a notebook.",
    badges: [["Customer management: all plans", true], ["Credit, packages & stamp card: Pro and above", false], ["Loyalty points: Enterprise", false]],
    shots: { phoneSecond: true, shots: ["o8-customers-crop.jpg", "c6-confirm.jpg"] },
    steps: [
      ["o8-customers-crop.jpg", "Found in a search", "Search by name, phone or pet name and open the record in one tap."],
      ["o3-detail.jpg", "Everything on the booking", "Credit balance, points, digital stamp card, package visits and the pet's notes."],
      ["c6-confirm.jpg", "Used when customers book", "Customers can use packages and points when booking, and add grooming requests."],
    ],
    points: [
      "A customer's record is created automatically the first time they book online",
      "Each pet's allergies and notes appear on every booking",
      "Special prices for individual regulars",
      "Each shop's data is kept separate — other shops can't see your customers",
    ],
    faq: [2, 4],
    seo: {
      title: "Customer Management for Grooming Shops | Credit, Packages, Stamp Card · PettoBook",
      description: "Customer management for grooming shops: customer and pet records saved automatically, with credit, packages, digital stamp cards and loyalty points on one page. Search by name or phone. Customer management is on every plan.",
      keywords: "grooming shop CRM,pet salon customer management,prepaid credit,grooming packages,digital stamp card,loyalty points",
    },
  },
  {
    slug: "directory", tab: "Directory listing", kicker: "Grooming shop directory",
    title: "Pet owners nearby find you, and book.",
    lead: "PettoBook has a public directory of grooming shops. Owners nearby can find you by district, see your photos and services, and book directly. You're listed automatically when you open your shop — free.",
    badges: [["All plans", true]],
    shots: { phones: true, shots: ["c1-shop.jpg", "c3-spa.jpg"] },
    steps: [
      ["c1-shop.jpg", "Owners search by area", "Each city and district with listed shops has its own page, so pet owners nearby find you."],
      ["c3-spa.jpg", "They see your services and photos", "Services, spa options, prices and photos — no need to WhatsApp you first."],
      ["c5-time.jpg", "They book directly", "Shops with online booking switched on can be booked straight away."],
    ],
    points: [
      "Listed automatically when you open your shop — free",
      "Already in the directory? Claim your listing by uploading your business registration",
      "Every shop gets its own booking page and QR code",
    ],
    faq: [3, ["My shop is already in the directory. How do I claim it?", "Tap “Claim this shop” on your listing and upload your business registration certificate. Once it's verified, the listing is linked to your account for you to manage."]],
    seo: {
      title: "Free Directory Listing for Pet Grooming Shops · PettoBook",
      description: "List your grooming shop free in the PettoBook directory: pet owners nearby find you by area, see your services and photos, and book directly. Already listed? Claim it with your business registration.",
      keywords: "pet grooming directory,list grooming shop free,grooming shop marketing,local pet groomers,find a groomer near me",
    },
  },
];

const UI = {
  lang: "en",
  overviewTab: "Overview",
  overviewTitle: "Pet Grooming Booking Software | Start Free · PettoBook",
  signup: "Start free",
  demo: "See the demo shop →",
  openDemo: "open the demo shop",
  navPricing: "Pricing", navHelp: "Help", navDirectory: "Groomer directory",
  heroNote: "14-day free Pro trial · No credit card · Set up in 3 minutes",
  playApp: "Android shop app", playGet: "Get it on Google Play",
  realScreens: "Everything below is a real screen from the app (demo shop, Chinese interface).",
  howItWorks: "How it works",
  keyPoints: "Key points",
  realShop: "A real shop",
  faqTitle: "Questions shop owners ask",
  taskFinalTitle: "Open your shop in 3 minutes. Use it today.",
  taskFinalSub: "See how PettoBook handles the rest of your work:",
  seePricing: "See pricing →",
  more: "Learn more →",
  baTitle: "Your day now vs with PettoBook",
  baSub: "Bookings, quotes, confirmations, records — the work you do by hand, handed to the system.",
  whyTitle: "Made for grooming shops",
  bookingTitle: "How customers book: 8 steps, 1 minute",
  bookingSub: "This is what your customers see. Want to try it yourself?",
  dayTitle: "A day at the grooming shop",
  daySub: "From opening to closing, PettoBook saves you time at every step.",
  dayVideo: ["daily-en.mp4", "daily-en-poster.jpg", "A day at the grooming shop: from opening to closing, one app runs it all"],
  heroVideo: ["daily-en.mp4", "daily-en-poster.jpg"],
  helpLine: "Step-by-step guides and troubleshooting: ", helpLink: "Help centre (Chinese) →",
  pricingTitle: "Simple pricing. Free to open.",
  pricingSub: "Pay monthly. No long contract.",
  recommended: "Recommended",
  perMonth: " /month",
  aiNote: "* WhatsApp AI auto-reply isn't included in any plan: it needs a separately paid third-party Chatwoot or WATI account, connected to PettoBook.",
  finalTitle: "Tonight, after you close, let customers book themselves.",
  finalSub: "Open your shop in 3 minutes — pick a template and your prices are set.",
  foot: [["/", "Home"], ["/en/pricing", "Pricing"], ["/en/help", "Help centre"], ["/directory", "Find a groomer"], ["/blog", "Grooming tips"]],
  switchTo: ["/features", "中文"],
};

const BA = {
  before: ["😩 Before", ["Replying “any slots tomorrow?” on WhatsApp while grooming", "Asking every customer for weight and breed before quoting", "Bookings in a notebook — clashes found too late", "Messages after closing answered next morning, by which time the customer has gone elsewhere", "Getting the price poster redesigned every time a price changes"]],
  after: ["😊 Now", ["Customers see free slots and book themselves", "Pick the pet and the price is worked out", "Each groomer's time is tracked — no double bookings", "Bookings 24 hours a day, confirmed on WhatsApp", "A price list in one tap, with a QR poster to share"]],
};

const CASE = {
  title: "PAWSIN Japanese Pet Grooming: customers book themselves",
  stats: [["463", "bookings (since May 2026)"], ["94%", "made online by customers"], ["99", "made between 9 pm and 9 am"], ["210", "customers"]],
  text: "99 bookings came in after closing time. Those customers used to wait until the next morning for a reply — now they pick a time themselves, and it's in the calendar when the shop opens.",
  seo: "463 bookings since May 2026: 94% made online by customers, 99 between 9 pm and 9 am, 210 customers in total.",
};

const SEO = {
  title: "Pet Grooming Software for Salons & Mobile Groomers | Free 14-Day Trial · PettoBook",
  description: "Booking software for pet grooming shops: prices by weight and breed, 24/7 online self-booking, automatic WhatsApp confirmations, and a price list and QR code in one tap. At a real shop, 94% of bookings are made online by customers. Start free.",
  keywords: "pet grooming software,dog grooming software,grooming salon software,grooming booking system,dog grooming appointment app,pet salon POS,groomer scheduling software,WhatsApp booking confirmation",
  crumb: "Grooming shop booking software", home: "Home",
  whyHeading: "Why grooming shops choose PettoBook", stepsHeading: "How customers book online (8 steps)", plansHeading: "Plans",
  caseHeading: "A real shop: PAWSIN Japanese Pet Grooming",
  videoName: "A day at the grooming shop · PettoBook", dailyVideoName: "A day at the grooming shop: from opening to closing, one app runs it all · PettoBook",
  dailyVideoDesc: "Today's bookings at opening, 24/7 self-booking, walk-ins, groomer scheduling, one-tap status and customer records saved automatically.",
};

export const EN = { HERO, FEATURES, BOOKING_STEPS, DAY, FAQ, PLANS, TASK_PAGES, UI, BA, CASE, SEO, base: "/en/features" };
